import { JSX } from 'react';
import {
    BooleanInput,
    Edit,
    SimpleForm,
    TextInput,
    required,
    useRecordContext,
    useNotify,
    useRedirect,
} from 'react-admin';
import { useWatch } from 'react-hook-form';
import { Alert, Typography } from '@mui/material';
import { AdminRole } from '../../types/entities';
import { AclResourceTreeInput } from './components/acl/AclResourceTreeInput';

/**
 * Editing an ACL role: a name, Magento's "Resource Access: All or Custom", and the tree.
 *
 * The refusals HQ-API can answer with here are sentences an admin needs to read, so `onError`
 * shows the API's own message. `adminRoleDataProvider` rejects with an `HttpError` carrying
 * `hydra:description` rather than swallowing a non-2xx, which is what makes that possible; the
 * template most HQ providers copy would have turned every one of them into an empty result.
 */

/**
 * The All toggle drives whether the tree is editable, so the form has to watch it rather than
 * read the record, which does not change as the toggle moves.
 */
const RoleFields: () => JSX.Element = (): JSX.Element => {
    const record = useRecordContext<AdminRole>();
    const isAll: boolean = useWatch({ name: 'isAll' }) === true;

    return (
        <>
            { record?.isSystem &&
                <Alert severity="info" sx={ { mb: 2, width: '100%' } }>
                    This role is part of the application and cannot be changed.
                </Alert>
            }
            <TextInput source="name" validate={ [required()] } fullWidth />
            <TextInput source="description" multiline fullWidth />
            <BooleanInput
                source="isAll"
                label="Access to everything"
                helperText="On, this role reaches every resource, including ones added later. Off, choose them below."
            />
            <Typography variant="subtitle2" sx={ { mt: 2 } }>Resource access</Typography>
            <AclResourceTreeInput source="resources" disabled={ isAll } />
        </>
    );
};

export const AdminRoleEdit: () => JSX.Element = (): JSX.Element => {
    const notify = useNotify();
    const redirect = useRedirect();

    return (
        <Edit
            mutationMode="pessimistic"
            mutationOptions={ {
                onSuccess: (): void => {
                    notify('Role saved', { type: 'info' });
                    redirect('list', 'admin_roles');
                },
                // The API's own wording. "You cannot grant what you do not hold: HQ_Admin::roles"
                // is the whole explanation, and a generic failure notice would throw it away.
                onError: (error: any): void => notify(
                    typeof error?.message === 'string' && error.message.length > 0
                        ? error.message
                        : 'Could not save the role',
                    { type: 'error' }
                ),
            } }
        >
            <SimpleForm>
                <RoleFields />
            </SimpleForm>
        </Edit>
    );
}
